import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemQuestionnaire from "Reporting/Components/ReportItemQuestionnaire";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { Select } from "shared/Select/Select";

/*
 * Table displaying different items in a report with the functionality to sort and filter
 */
export default function ReportTableQuestionnaire({
  reports,
  title,
  totals,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([]);

  // helper to collect all unique options for a given field
  const getAllOptions = (field) => {
    if (!Array.isArray(reports)) return [];
    if (field === "pin") {
      return Array.from(new Set(reports.map((r) => r.pin).filter(Boolean)));
    } else if (field === "diamond") {
      return Array.from(new Set(reports.map((r) => r.diamond).filter(Boolean)));
    }
    return [];
  };

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initial = {};
    const pinOptions = getAllOptions("pin");
    if (pinOptions.length > 0) initial.pin = [...pinOptions];
    const diamondOptions = getAllOptions("diamond");
    if (diamondOptions.length > 0) initial.diamond = [...diamondOptions];
    return initial;
  });

  useEffect(() => {
    setSelectedFilters((prev) => {
      const next = { ...prev };

      const pinOptions = getAllOptions("pin");
      if (pinOptions.length > 0) {
        const prevPins = Array.isArray(prev.pin) ? prev.pin : [];
        const kept = prevPins.filter((p) => pinOptions.includes(p));
        const added = pinOptions.filter((p) => !prevPins.includes(p));
        next.pin = [...kept, ...added];
      } else {
        delete next.pin;
      }

      const diamondOptions = getAllOptions("diamond");
      if (diamondOptions.length > 0) {
        const prevDiamonds = Array.isArray(prev.diamond) ? prev.diamond : [];
        const keptD = prevDiamonds.filter((d) => diamondOptions.includes(d));
        const addedD = diamondOptions.filter((d) => !prevDiamonds.includes(d));
        next.diamond = [...keptD, ...addedD];
      } else {
        delete next.diamond;
      }

      return next;
    });
  }, [reports]);

  const [sortedReports, setSortedReports] = useState(reports || []);

  const { groupedHeaders, allColumns } = useMemo(() => {
    if (!reports || reports.length === 0) {
      return { groupedHeaders: [], allColumns: [] };
    }

    const byHeader = reports.reduce((acc, r) => {
      if (!acc[r.questionColumnHeader]) {
        acc[r.questionColumnHeader] = new Set();
      }
      acc[r.questionColumnHeader].add(r.questionColumn);
      return acc;
    }, {});

    const grouped = Object.entries(byHeader).map(([headerName, questions]) => {
      const cols = Array.from(questions).map((question) => ({
        name: question,
        headerName: question,
        getValue: (row) => {
          if (row.questionColumn !== question) return "";
          return row.responseValue;
        },
      }));

      return {
        headerName,
        columns: cols,
        colSpan: cols.length,
      };
    });

    return {
      groupedHeaders: grouped,
      allColumns: grouped.flatMap((g) => g.columns),
    };
  }, [reports]);

  useEffect(() => {
    let result = Array.isArray(reports) ? [...reports] : [];

    // If any selected filter is explicitly empty, return no results
    const hasEmptyFilter = Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length === 0
    );
    if (hasEmptyFilter) {
      setSortedReports([]);
      return;
    }

    // apply selectedFilters (pin, diamond)
    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((r) => {
        let compareVal;
        switch (field) {
          case "pin":
            compareVal = r.pin;
            break;
          case "diamond":
            compareVal = r.diamond;
            break;
          default:
            compareVal = "";
        }
        return values.includes(compareVal);
      });
    });

    // apply sorting (multi-sort)
    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (!s.field || s.direction === "none") continue;
          let aVal, bVal;
          switch (s.field) {
            case "IBO":
              aVal = a.IBO;
              bVal = b.IBO;
              break;
            case "Name":
              aVal = a.name;
              bVal = b.name;
              break;
            default:
              aVal = 0;
              bVal = 0;
          }

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
  }, [reports, sort, selectedFilters]);

  const prevSortedReportsRef = useRef([]);

  useEffect(() => {
    const prev = prevSortedReportsRef.current;

    const changed =
      prev.length !== sortedReports.length ||
      JSON.stringify(prev) !== JSON.stringify(sortedReports);

    if (changed) {
      onSortedReportsChange?.(title, sortedReports);
      prevSortedReportsRef.current = sortedReports;
    }
  }, [sortedReports, title, onSortedReportsChange]);

  const updateFilter = (field, value) => {
    setSelectedFilters((prev) => {
      const current = prev[field] || [];
      let allOptions = getAllOptions(field);

      let newValues;
      if (value === "All") {
        // Toggle all: if all selected, clear; else select all
        if (current.length === allOptions.length) {
          newValues = [];
        } else {
          newValues = [...allOptions];
        }
      } else {
        // Toggle individual value
        if (current.includes(value)) {
          newValues = current.filter((v) => v !== value);
        } else {
          newValues = [...current, value];
        }
        // If after toggling, all are selected, treat as all selected
        if (newValues.length === allOptions.length) {
          newValues = [...allOptions];
        }
      }
      // Always remove any accidental 'All' string from the array
      newValues = newValues.filter((v) => v !== "All");
      return { ...prev, [field]: newValues };
    });
  };

  const toggleSort = (field) => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc" }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;
        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = { field, direction: nextDirection };
        }
      }

      return newSort;
    });
  };

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);
    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon =
      sort[index].direction === "asc"
        ? faChevronUp
        : sort[index].direction === "desc"
        ? faChevronDown
        : faMinus;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  // Calculate page breaks for PDF/export, using a single ref
  const calculateBreaks = (ref, rowCount, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      // Get both the header and the title (headerInfo) heights
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      // headerInfo is outside the tableRef, so get it from the parent
      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl)
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
      }

      let staticHeight = headerInfoHeight + headerHeight;
      if (headerInfoHeight === 0) {
        staticHeight += 50;
      }

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }

        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);

          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }
        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(tableRef, sortedReports.length, setPageBreakIndexes);
  }, [sortedReports]);

  return (
    <div>
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{title}</div>
        </div>
      </div>

      {totals && totals.length > 0 && (
        <div className={styles.totals}>
          {totals.map((t, i) => (
            <div key={i} className={styles.totalQuestionnaire}>
              {t.questionnaire}:
              {t.questions.map((q, j) => (
                <div key={j} className={styles.totalQuestion}>
                  <div className={styles.questionText}>{q.question}</div>
                  {q.answers.map((a, k) => (
                    <div key={k} className={styles.totalAnswer}>
                      {a.answer}: {a.total}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div
              className={styles.upperHeaderFiller}
              style={{ flex: `0 0 75%`, maxWidth: `75%` }}
            />
            {groupedHeaders.map((header) => {
              const headerColCount = header.columns.length;
              const totalColCount = allColumns.length;
              const widthPercent =
                totalColCount > 0 ? (headerColCount / totalColCount) * 25 : 0;

              return (
                <div
                  key={header.headerName}
                  className={styles.headerGroup}
                  style={{
                    flex: `0 0 ${widthPercent}%`,
                    maxWidth: `${widthPercent}%`,
                  }}
                >
                  <span className={styles.headerLabel}>
                    {header.headerName}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.header}>
            <div className={styles.ibo} onClick={() => toggleSort("IBO")}>
              IBO {renderSortIcon("IBO")}
            </div>
            <div className={styles.name} onClick={() => toggleSort("Name")}>
              Name {renderSortIcon("Name")}
            </div>

            <div className={styles.attending}>Attending</div>

            {allColumns.map((col) => (
              <div
                key={col.name}
                className={styles.col}
                style={{
                  width: `${25 / allColumns.length}%`,
                  textAlign: "center",
                }}
              >
                {col.headerName}
              </div>
            ))}
          </div>

          {sortedReports.map((row, i) => (
            <React.Fragment key={i}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemQuestionnaire
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                  groupedHeaders={groupedHeaders}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableQuestionnaire.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
